import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { theme, useHealthToolsCache } from '@hhgtech/hhg-components'
import { Text, Button } from '@hhgtech/hhg-components/mantine'
import { ChevronRight, ChevronLeft } from '@hhgtech/icons/arrow'
import { Carousel, Embla } from '@mantine/carousel'
import { Box, Flex } from '@mantine/core'
import innertext from 'innertext'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import { formatArticle } from 'api/dataTransform'
import { getHelloSitesUrl } from 'api/utils/paths'
import { CardArticle } from 'components/atoms/CardArticleV2'
import { CardArticleAuthorName } from 'components/atoms/CardArticleV2/CardArticleAuthor'
import { NextImageWrap } from 'components/atoms/imageWrap'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath, DEFAULT_IMG } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { ArticleApi } from 'interfaces/types'
import { useModalStore } from 'state/modal'
import { getAuthorFromArticleApi } from 'utils/author'
import { fastdom } from 'utils/fastdom'

const SECONDS_TO_CHANGE_SLIDE = 14

const GuideAndSupportArticles = ({
  colWidth,
  suggestedArticles,
  titleArticle,
  descriptionArticle,
}: {
  colWidth?: string
  suggestedArticles: ArticleApi[]
  titleArticle?: string
  descriptionArticle?: string
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const isMobile = useIsMobile()
  const { locale } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)

  const { formatMessage: f } = useIntl()
  const [embla, setEmbla] = useState<Embla | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  const setShouldOnboardingModalOpen = useModalStore(
    (state) => state.setShouldOnboardingModalOpen,
  )

  const chunkSuggestedArticles = useMemo(() => {
    if (!Array.isArray(suggestedArticles) || suggestedArticles.length === 0)
      return []

    const result: ArticleApi[][] = []
    let index = 0

    while (index < suggestedArticles.length) {
      const chunkSize = index === 0 ? 2 : 3
      result.push(suggestedArticles.slice(index, index + chunkSize))
      index += chunkSize
    }

    // return [result[0]] // Test only have 1 slide
    return result
  }, [suggestedArticles])

  const moreThanOneSlide = chunkSuggestedArticles.length > 1

  const handleScroll = useCallback(() => {
    if (!embla) return
    setCurrentSlide(embla.selectedScrollSnap())
  }, [embla])

  const triggerProgressBar = useCallback(() => {
    // Reset to zero first
    if (progressBarRef.current) {
      progressBarRef.current.style.transition = 'none'
      progressBarRef.current.style.width = '0'
    }
    requestAnimationFrame(() => {
      // Then trigger animation to 100%
      if (progressBarRef.current) {
        progressBarRef.current.style.transition = `all ${SECONDS_TO_CHANGE_SLIDE}s linear`
        progressBarRef.current.style.width = '100%'
      }
    })
  }, [])

  useEffect(() => {
    if (!embla || !moreThanOneSlide) return

    triggerProgressBar()
    const intervalId = setInterval(() => {
      triggerProgressBar()
      embla.scrollNext()
    }, SECONDS_TO_CHANGE_SLIDE * 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [
    embla,
    currentSlide,
    triggerProgressBar,
    moreThanOneSlide,
    Boolean(progressBarRef.current),
  ])

  useEffect(() => {
    if (!embla) return
    const handleResize = () => {
      fastdom.mutate(() => embla.reInit())
    }
    embla.on('scroll', handleScroll)
    wrapperRef.current?.addEventListener('resize', handleResize)
    return () => {
      embla.off('scroll', handleScroll)
      wrapperRef.current?.removeEventListener('resize', handleResize)
    }
  }, [embla])

  const goToWeightGain = useCallback(() => {
    setShouldOnboardingModalOpen(false)
  }, [])

  const width = useMemo(() => {
    return colWidth || isMobile ? '100%' : '56%'
  }, [isMobile, colWidth])

  return (
    <Flex
      p={16}
      direction="column"
      bg={theme.colors.pink100}
      w={width}
      miw={width}
      maw={width}
      sx={{ borderRadius: isMobile ? 0 : 16 }}
      ref={wrapperRef}
    >
      <Text
        as="h3"
        size={isMobile ? 'h4' : 'h5'}
        color={theme.colors.gray800}
        weight="semiBold"
      >
        {titleArticle ||
          f({
            id: 'tools.suggestedArticles',
            defaultMessage: 'Suggested articles',
          })}
      </Text>
      <Box mx={-4}>
        <Carousel
          loop
          containScroll="trimSnaps"
          draggable={false}
          dragFree={false}
          withControls={false}
          getEmblaApi={setEmbla}
        >
          {chunkSuggestedArticles.map((articlesApi, slideNumber) => {
            return (
              <Carousel.Slide key={slideNumber} px={4}>
                {slideNumber === 0 && (
                  <Flex gap={4} direction="column" align="flex-start" pt={4}>
                    <Text
                      size={isMobile ? 'p3' : 'p4'}
                      color={theme.colors.gray600}
                    >
                      {descriptionArticle ||
                        f({
                          id: 'pregnancyTool.weightGainBanner.message',
                          defaultMessage:
                            'You may have heard that you should gain 11 to 15 kg when you are pregnant.',
                        })}
                    </Text>
                    <Link
                      className="pointer-event-child-none"
                      data-event-category="Health Tool"
                      data-event-action="Due Date - Guide and Support"
                      data-event-label="Calculate Your Weight Gain"
                      href={getHealthToolLink(HEALTH_TOOL_TYPE.PWG)}
                      onClick={goToWeightGain}
                    >
                      <Button
                        variant="white"
                        size="sm"
                        style={{ padding: 0, minHeight: 22, height: 'auto' }}
                        fullWidth={false}
                        theme="marryBaby"
                        styles={{
                          label: {
                            whiteSpace: 'normal',
                          },
                        }}
                      >
                        <Text
                          as="span"
                          size={isMobile ? 's3' : 's4'}
                          weight="semiBold"
                          color={theme.colors.pink800}
                        >
                          {f({
                            id: 'pregnancyTool.weightGainBanner.button',
                            defaultMessage: 'Calculate your weight gain',
                          })}
                          <ChevronRight
                            color={theme.colors.pink800}
                            size={19}
                            style={{
                              verticalAlign: 'middle',
                            }}
                          />
                        </Text>
                      </Button>
                    </Link>
                  </Flex>
                )}
                <Box mt={16} sx={{ borderRadius: 16, overflow: 'hidden' }}>
                  {articlesApi.map((post, index) => {
                    const article = formatArticle(post)
                    const author = getAuthorFromArticleApi({
                      article: post,
                      locale,
                      version: 'short',
                    })
                    return (
                      <Box
                        key={post.ID}
                        bg={theme.colors.white}
                        p={16}
                        sx={{
                          borderTop:
                            index > 0
                              ? `1px solid ${theme.colors.neutral100}`
                              : undefined,
                        }}
                      >
                        <CardArticle
                          type="horizontalFooter"
                          item={{
                            ID: parseInt(`${article.id}`),
                            image: (
                              <NextImageWrap
                                src={article.featureImage || DEFAULT_IMG}
                                fill
                                alt={article.postName}
                              />
                            ),
                            permalink: article.permalink,
                            title: innertext(
                              article.postTitle || article.postName || '',
                            ),
                            category: article.category,
                            author: {
                              name: <CardArticleAuthorName {...author} />,
                              permalink: author.avatar_permalink,
                              avatar: author.avatar,
                              indicator: author.indicator,
                              avatarAlt: author.avatar_alt,
                            },
                          }}
                          itemTracking={{
                            image: {
                              'data-event-category': 'Health Tool',
                              'data-event-action':
                                'Due Date - Guide and Support - Click Article',
                              'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                            },
                            title: {
                              'data-event-category': 'Health Tool',
                              'data-event-action':
                                'Due Date - Guide and Support - Click Article',
                              'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                            },
                            category: {
                              'data-event-category': 'Health Tool',
                              'data-event-action':
                                'Due Date - Guide and Support - Click Article Category',
                              'data-event-label': `${currentDomain}${basePath}${article.category.permalinkDecode}`,
                            },
                            authorAvatar: {
                              'data-event-category': 'Health Tool',
                              'data-event-action':
                                'Due Date - Guide and Support - Click See Doctor',
                              'data-event-label': `${currentDomain}${basePath}${author.avatar_permalink}`,
                            },
                          }}
                          bookmarkButtonProps={{
                            style: { display: 'none' },
                          }}
                        />
                      </Box>
                    )
                  })}
                </Box>
              </Carousel.Slide>
            )
          })}
        </Carousel>
      </Box>

      {moreThanOneSlide && (
        <Flex mt={isMobile ? 16 : 80} gap={24} align="center">
          <Text size="p2" weight="semiBold" color={theme.colors.gray400}>
            {String(currentSlide + 1).padStart(2, '0')}/
            {String(chunkSuggestedArticles.length).padStart(2, '0')}
          </Text>
          <Box sx={{ flex: 1 }} bg={theme.colors.pink50}>
            <Box
              ref={progressBarRef}
              h={1}
              w={moreThanOneSlide ? 0 : '100%'}
              bg={theme.colors.pink800}
            />
          </Box>
          <Flex direction="row" gap={16}>
            <Flex
              w={32}
              h={32}
              align="center"
              justify="center"
              bg={theme.colors.white}
              sx={{ borderRadius: '100%', cursor: 'pointer' }}
              onClick={() => embla?.scrollPrev()}
            >
              <ChevronLeft size={20} color={theme.colors.gray600} />
            </Flex>
            <Flex
              w={32}
              h={32}
              align="center"
              justify="center"
              bg={theme.colors.white}
              sx={{ borderRadius: '100%', cursor: 'pointer' }}
              onClick={() => embla?.scrollNext()}
            >
              <ChevronRight size={20} color={theme.colors.gray600} />
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

export default GuideAndSupportArticles
